/* eslint-disable react/jsx-props-no-spreading */
/* global ChoiceOption */
import React, { useState, useEffect } from 'react';
import {
  Popover, Card, CardActions, Box, CircularProgress,
  Alert, Typography, Menu, MenuItem,
} from '@mui/material';
import { DateRange } from '@mui/lab/DateRangePicker';
import DateRangePicker from '../date-range-picker';
import DateRangeFn from '../../../models/date-range';
import useFilterValues from './hooks/use-filter-values';
import GlobalFilter from '../global-filter';
import ColumnFilterFiltersBox from './column-filter-filters-box';
import ColumnFilterSelectedBox from './column-filter-selected-box';
import { DatePickers, getSelectedValues, buildDateRanges } from './helpers';
import {
  FilterChip, SqlOptions, ChangeEvent, SystemTypeEnum,
} from './column-filter.types';
import Button from '../../../components/button';

interface Props {
  columnDataChips: FilterChip[];
  category: string;
  formData: SqlOptions;
  anchor: any;
  onClose: () => void;
  onSave: (selectedValues: ChoiceOption[]) => void;
  type: SystemTypeEnum;
}

const ColumnFilterValues: React.FC<Props> = ({ ...props }) => {
  const {
    columnDataChips, category, type, formData,
  } = props;
  const [selected, setSelected] = useState<ChoiceOption[]>([]);
  const [filter, setFilter] = useState({ globalFilter: '' });
  const [datePicker, setDatePicker] = useState(false);

  const existingDateRanges: string[] = buildDateRanges(type, category);

  const {
    data: values, isLoading, error, refetch, isSuccess,
  } = useFilterValues({
    formData, category, type, filter,
  });

  useEffect(() => {
    const selectedValues = getSelectedValues(category, columnDataChips);
    setSelected(selectedValues as ChoiceOption[]);
  }, []);

  const handleChange = (option: ChoiceOption) => (e: ChangeEvent) => {
    if (e.target.checked) {
      setSelected([...selected, option]);
    } else {
      setSelected(selected.filter((item) => item.value !== e.target.name));
    }
  };

  const handleDatePickerChange = (e: React.MouseEvent<HTMLLIElement>) => {
    const { filterCategory } = (e.target as HTMLLIElement).dataset;
    if (filterCategory === 'Custom') {
      setDatePicker(true);
    } else {
      props.onSave([{
        label: filterCategory as string,
        value: filterCategory as string,
      }]);
    }
  };

  const handleSetCustomFilter = (range: DateRange<Date>) => {
    const { fromDate, toDate } = DateRangeFn.fromVanillaDateArray(range).toDBFormat();

    setDatePicker(false);
    props.onSave([{
      label: 'Custom',
      value: `${fromDate}, ${toDate}`,
    }]);
  };

  const handleClick = () => {
    if (error) {
      refetch();
    } else {
      props.onSave(selected);
    }
  };

  return (
    <>
      <DateRangePicker
        modalOpen={datePicker}
        homeRangeDates={existingDateRanges}
        handleSetRange={handleSetCustomFilter}
        handleCloseModal={() => setDatePicker(false)}
      />
      <>
        {DatePickers.includes(category) ? (
          <Menu
            anchorEl={props.anchor}
            open={!!category}
            onClose={props.onClose}
          >
            {values?.map((opt: ChoiceOption) => (
              <MenuItem
                key={opt.value}
                onClick={handleDatePickerChange}
                data-filter-category={opt.value}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        ) : (
          <Popover
            anchorEl={props.anchor}
            open={!!category}
            onClose={props.onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Card variant="outlined" sx={{ p: 2, width: '450px' }}>
              {(filter.globalFilter.length || values?.length > 6) && (
                <GlobalFilter
                  form={props.formData.restForm}
                  setGlobalFilter={(search: string) => setFilter({ globalFilter: search ?? '' })}
                  placeholder="Search"
                  resetFilter={false}
                />
              )}
              {isSuccess && !error && (
                <>
                  {((values.length > 6) && !!selected.length) && (
                    <ColumnFilterSelectedBox
                      values={values}
                      selected={selected}
                      onChange={handleChange}
                    />
                  )}
                </>
              )}
              <Typography pt={1} pb={1}>Filters</Typography>
              {error && <Alert severity="error">Error loading values for filter.</Alert>}
              {isLoading && (
                <Box pt={2} display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              )}
              {isSuccess && !error && (
                <>
                  {values.length ? (
                    <ColumnFilterFiltersBox
                      values={values}
                      selected={selected}
                      onChange={handleChange}
                    />
                  ) : (<span>No results.</span>)}
                </>
              )}
              <CardActions sx={{ pb: 0, pt: 2, justifyContent: 'flex-end' }}>
                <Button
                  color="secondary"
                  variant="text"
                  onClick={handleClick}
                  disabled={!selected.length && !error}
                >
                  {error ? 'Retry' : 'Done'}
                </Button>
              </CardActions>
            </Card>
          </Popover>
        )}
      </>
    </>
  );
};

export default ColumnFilterValues;
